import { action, observable } from 'mobx';
import { provideSingleton, inject } from '../utils/decorators';
import { IObjektVorlage, IObjekt, IArbeitsmittel } from '../models/ApiTypes';
import KalibrierGegenstand from '../models/KalibrierGegenstand';
import { round } from '../utils/round';
import { IBpsReferenzkraftMesswerte } from '../models/ApiTypesBpsPlatte';
import * as ApiTypesBps from '../models/ApiTypesBpsPlatte';
import { ApiStore } from './ApiStore';
import * as ApiTypeFactory from '../models/ApiTypeFactory';
import { VorgangStore } from './VorgangStore';

/** Singleton für Kalibrierung Plattenbremsprüfstand mit Referenzkraft-Verfahren nach 2021er Richtlinie */
@provideSingleton(BpsReferenzkraftKalibrierungPlatteStore)
export class BpsReferenzkraftKalibrierungPlatteStore {

  @inject(ApiStore) apiStore: ApiStore;
  @inject(VorgangStore) vorgangStore: VorgangStore;

  @observable objektVorlage: IObjektVorlage | undefined = undefined;
  @observable kalibrierGegenstand: KalibrierGegenstand | undefined = undefined;
  @observable kraftMessGeraet: IArbeitsmittel | undefined = undefined; // @todo: Eigene Wrapperklasse für AM wie für KalGegenstand?
  @observable readOnly: boolean = false;

  private abrufTimeout: number = 0;

  @observable datensatz: ApiTypesBps.IBpsReferenzkraftMessungPlatte = ApiTypeFactory.leererDatensatzBPSReferenzkraftPlatte();

  @action clear() {
    this.datensatz = ApiTypeFactory.leererDatensatzBPSReferenzkraftPlatte();
  }

  /** Ruft mit der aktuellen Eingabe Sollwerte aus dem Back-End ab */
  async messpunkteAbrufen() {
    const { datensatz, kalibrierGegenstand } = this;
    if (kalibrierGegenstand && kalibrierGegenstand.objekt && datensatz.messbereich) {
      let messwerte: IBpsReferenzkraftMesswerte = {
        messbereich: datensatz.messbereich,
        objekt: kalibrierGegenstand.objekt.objektguid
      };

      const response = await this.apiStore.bpsReferenzkraftMesspunktePlatte(messwerte);
      if (response.ok) {
        return response;
      }
    }
    return undefined;
  }

  /** Ruft die Beurteilung der Messpunkte für die beiden Fahrspuren vom Backend ab */
  @action async beurteilungAbrufen() {
    // @todo: prüfen was noch gebraucht wird und auswahl kmg ggf. in Back-End auslagern
    if ((!this.kraftMessGeraet) || (!this.kalibrierGegenstand) || (!this.kalibrierGegenstand.objekt)) {
      return;
    }
    this.datensatz.objekt = this.kalibrierGegenstand.objekt.objektguid;
    this.datensatz.kmg = this.kraftMessGeraet.arbeitsmittelguid;
    const response = await this.apiStore.bpsReferenzkraftBeurteilungPlatte(this.datensatz);
    if (response && response.ok) {
      this.setBeurteilung(response.json);
    }
  }

  @action setBeurteilung(beurteilung: ApiTypesBps.IBpsReferenzkraftBeurteilungResponse) {
    const { plattensatz, plattensatzJustage, gesamtErgebnis } = this.datensatz;
    beurteilung.links.forEach((e, index) => { plattensatz.links.messpunkte[index].beurteilung = e.bewertung; });
    beurteilung.rechts.forEach((e, index) => { plattensatz.rechts.messpunkte[index].beurteilung = e.bewertung; });
    gesamtErgebnis.length = beurteilung.gesamt.length;
    beurteilung.gesamt.forEach((e, index) => { gesamtErgebnis[index] = e; });
    // Nach Justage
    if (beurteilung.justage) {
      beurteilung.justage.links.forEach((e, index) => { plattensatzJustage.links.messpunkte[index].beurteilung = e.bewertung; });
      beurteilung.justage.rechts.forEach((e, index) => { plattensatzJustage.rechts.messpunkte[index].beurteilung = e.bewertung; });
      plattensatzJustage.gesamtErgebnis.length = beurteilung.justage.gesamt.length;
      beurteilung.justage.gesamt.forEach((e, index) => { plattensatzJustage.gesamtErgebnis[index] = e; });
    }
  }

  /** Berechnet alle Referenzwerte neu, z.B. wenn ein Messwert oder der Rollendurchmesser verändert wurden */
  berechneReferenzWerte() {
    // @todo: umbenennen oder entfernen?

    const { plattensatz, plattensatzJustage, justageInfo } = this.datensatz;

    if (plattensatz.links.messpunkte.length === 0 || plattensatz.rechts.messpunkte.length === 0) {
      return;
    }

    // Lambda für Berechnung der Werte zu einem gegebenen Messpunkt
    let mwb = (mw: ApiTypesBps.IBpsReferenzkraftPlattensatzMesswert) => {
      if ((mw.sensor === null) || (mw.bps === null) || (isNaN(mw.sensor)) || (isNaN(mw.bps))) {
        mw.referenzwert = mw.messwertabweichung = null;
      } else {
        mw.referenzwert = round(mw.sensor, 0);
        mw.messwertabweichung = round(mw.bps - mw.referenzwert, 0);
      }
    };

    plattensatz.links.messpunkte.forEach(mp => { mp.messwerte.forEach((mw) => { mwb(mw); }); });
    plattensatz.rechts.messpunkte.forEach(mp => { mp.messwerte.forEach((mw) => { mwb(mw); }); });
    if (justageInfo === 'justage') {
      plattensatzJustage.links.messpunkte.forEach(mp => { mp.messwerte.forEach((mw) => { mwb(mw); }); });
      plattensatzJustage.rechts.messpunkte.forEach(mp => { mp.messwerte.forEach((mw) => { mwb(mw); }); });
    }

    // Um zu häufige Abrufe beu schnellen Änderungen zu vermeiden, wird die Beurteilung verzögert abgerufen
    if (this.abrufTimeout > 0) {
      window.clearTimeout(this.abrufTimeout);
      this.abrufTimeout = 0;
    }
    this.abrufTimeout = window.setTimeout( () => { this.beurteilungAbrufen(); }, 500);
  }

  messbereichGueltig() {
    return (this.datensatz.messbereich && this.datensatz.messbereich > 0);
  }

  @action setObjektVorlage(value: IObjektVorlage | undefined) {
    this.objektVorlage = value;
  }

  @action setKraftMessGeraet(kmg: IArbeitsmittel | undefined) {
    this.kraftMessGeraet = kmg;
  }

  @action setKalibrierGegenstand(value: IObjekt | undefined) {
    if (!value) {
      // Standardwerte setzen, falls noch kein Bremsenprüfstand gewählt wurde
      this.kalibrierGegenstand = undefined;
      this.datensatz.anzeige = 'analog';
      return;
    }
    this.kalibrierGegenstand = new KalibrierGegenstand(value);
    // Art der Anzeige speichern, diese hat Auswirkungen auf die Auswertung
    this.datensatz.anzeige = 'analog';
    let anzeigeart = this.kalibrierGegenstand.meta('anzeigeart');
    if (anzeigeart && anzeigeart.toLowerCase() === 'digital') {
      this.datensatz.anzeige = 'digital';
    }
  }

  @action setMessbereich(value: number) {
    this.datensatz.messbereich = value;
  }

  @action setReadOnly(value: boolean) {
    this.readOnly = value;
  }

  @action setJustageInfo(value: string) {
    this.datensatz.justageInfo = value;
    // Bei aktiver Justage wird eine zweite Eingabe angezeigt, da die Auswahl des Messbereichs nur einmal erfolgt,
    // müssen die Messpunkte hier entsprechend der Eingabe vor der Justage dimensioniert werden
    if (this.datensatz.justageInfo === 'justage') {
      let quellen = [this.datensatz.plattensatz.links.messpunkte, this.datensatz.plattensatz.rechts.messpunkte];
      let ziele = [this.datensatz.plattensatzJustage.links.messpunkte, this.datensatz.plattensatzJustage.rechts.messpunkte];
      for (let i = 0; i < quellen.length; i++) {
        // Wenn im Ziel (Messwerte nach Justage) Messpunkte fehlen, werden diese hinzugefügt
        let quelle = quellen[i];
        let ziel = ziele[i];
        for (let j = ziel.length; j < quelle.length; j++) {
          ziel.push({
            anzeige: quelle[j].anzeige,
            mittelwert: undefined,
            mittelwertabweichung: null,
            messunsicherheit: null,
            beurteilung: null,
            messwerte: [
              { bps: quelle[j].anzeige, sensor: null, referenzwert: null, messwertabweichung: null},
              { bps: quelle[j].anzeige, sensor: null, referenzwert: null, messwertabweichung: null},
              { bps: quelle[j].anzeige, sensor: null, referenzwert: null, messwertabweichung: null}
            ]
          });
        }
      }
    }
  }

  // umbenennen
  @action setMesswerte(value: Partial<ApiTypesBps.IBpsReferenzkraftMessungPlatte>) {
    const { datensatz } = this;
    // @todo
    // JSON.parse kann an dieser Stelle nicht verwendet werden, weil Daten auch teilweise gespeichert werden können
    // Das würde dann u.U. ein Teil-Interface erzeugen, mit dem die Eingabe nicht mehr funktioniert
    if (value.version) { datensatz.version = value.version; }
    if (value.messbereich) { datensatz.messbereich = value.messbereich; }
    if (value.plattensatz && value.plattensatz.links) { datensatz.plattensatz.links = value.plattensatz.links; }
    if (value.plattensatz && value.plattensatz.rechts) { datensatz.plattensatz.rechts = value.plattensatz.rechts; }
    if (value.pruefortparameter) {
      if (value.pruefortparameter.beginn) { datensatz.pruefortparameter.beginn = value.pruefortparameter.beginn; }
      if (value.pruefortparameter.ende) { datensatz.pruefortparameter.ende = value.pruefortparameter.ende; }
    }
    if (value.waegezelle) { datensatz.waegezelle = value.waegezelle; }
    if (value.kmg) { datensatz.kmg = value.kmg; }
    if (value.objekt) { datensatz.objekt = value.objekt; }
    if (value.justageInfo) { datensatz.justageInfo = value.justageInfo; }
    if (value.plattensatzJustage && value.plattensatzJustage.links) { datensatz.plattensatzJustage.links = value.plattensatzJustage.links; }
    if (value.plattensatzJustage && value.plattensatzJustage.rechts) { datensatz.plattensatzJustage.rechts = value.plattensatzJustage.rechts; }
    if (value.plattensatzJustage && value.plattensatzJustage.gesamtErgebnis) { datensatz.plattensatzJustage.gesamtErgebnis = value.plattensatzJustage.gesamtErgebnis; }
    if (value.gesamtErgebnis) { datensatz.gesamtErgebnis = value.gesamtErgebnis; }
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/stores/BpsReferenzkraftKalibrierungPlatteStore.ts